import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Fragment, useContext } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import DonutCustomLegend from '../../components/ApexCharts/CustomLegends/DonutCustomLegend';
import DonutChart from '../../components/ApexCharts/DonutChart';
import Button from '../../components/Button/Button';
import Carousel from '../../components/Carousel';
import ChartCard from '../../components/ChartCard';
import DefaultCard from '../../components/DefaultCard';
import FingerTipsLinks from '../../components/FingertipsLinks/FingertipsLinks';
import {
  PensionIndexProvider,
  PensionIndexStore,
} from '../../providers/pensionIndex';
import {
  ACCOUNT_ACTIVITY_ROUTE,
  PENSION_PROFILE_ROUTE,
  PENSION_ROUTE,
  RETIREMENT_PLANNING_ROUTE,
  RETIRE_ROUTE,
  TOTAL_REWARDS_ROUTE,
} from '../../routes/types';
import {
  addOrRemoveYearsToGivenDate,
  DateFormats,
  formatDate,
} from '../../utils/utils';
import CurrentAccruedPension from '../widgets/dashboards/CurrentAccruedPension';

const linksDataCurrentAccruedPension = [
  {
    linkHeader: 'Account History',
    route: ACCOUNT_ACTIVITY_ROUTE,
  },

  {
    linkHeader: 'Pension Profile',
    route: PENSION_PROFILE_ROUTE,
  },
];

const currentAccruedPensionData = [
  {
    title: 'Cash Balance Pension',
    subtitle: `As of ${formatDate(new Date(), DateFormats.SHORT)}`,
    monthlyBenefitPriceDescription: 'Pay Age 65',
    balance: 53185.62,
    monthlyBenefit: 355.21,
  },
  {
    title: 'Qualified Plan',
    subtitle: `As of ${formatDate(new Date(), DateFormats.SHORT)}`,
    monthlyBenefitPriceDescription: 'Pay Age 65',
    balance: '',
    monthlyBenefit: 2643.98,
  },
  {
    title: 'Non-Qualified Plan',
    subtitle: `As of ${formatDate(new Date(), DateFormats.SHORT)}`,
    monthlyBenefitPriceDescription: 'Pay Age 65',
    balance: '',
    monthlyBenefit: 602.95,
  },
];

const dataCompanyContributions = {
  grid: {
    padding: {
      left: 0,
    },
  },
  legend: {
    show: false,
  },

  dataLabels: {
    enabled: false,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        grid: {
          padding: {
            left: 0,
          },
        },
        legend: {
          show: false,
        },
        title: {},
      },
    },
  ],
  colors: ['#074CBC', '#F48462', '#07B4A0', '#EEA968', '#92E3A9'],
  series: [95072, 15905, 14791, 6500, 0],
  labels: [
    'Compensation',
    'Health and Welfare',
    'Retirement',
    'Equity',
    'Other Benefits',
  ],

  tooltip: {
    enabled: false,
  },

  plotOptions: {
    pie: {
      donut: {
        labels: {
          show: true,
          name: {
            offsetY: 10,
          },
          total: {
            show: true,
            showAlways: true,
            fontSize: '18px',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 500,
            label: `$132,268`,
            formatter: function (a: any) {
              return '';
            },
          },
        },
      },
    },
  },
};

const milestonesData = [
  {
    title: 'Earliest Retirement Date',
    age: 50,
    date: formatDate(new Date(), DateFormats.US),
    firstSubTitle: {
      title: 'Qualified Plan',
      amount: 993.25,
      plans: [
        { title: 'Cash Balance Plan', amount: 176.43 },
        { title: 'Legacy Plan', amount: 816.82 },
      ],
    },
    secondSubTitle: { title: 'Non-Qualified Plan ', amount: 243.18 },
  },
  {
    title: 'Unreduced Early Retirement Date',
    date: formatDate(
      addOrRemoveYearsToGivenDate(new Date(), 5),
      DateFormats.US
    ),
    age: 55,
    firstSubTitle: {
      title: 'Qualified Plan',
      amount: 1942.9,
      plans: [
        { title: 'Cash Balance Plan', amount: 248.1 },
        { title: 'Legacy Plan', amount: 1694.8 },
      ],
    },
    secondSubTitle: { title: 'Non-Qualified Plan ', amount: 412.78 },
  },
  {
    title: 'Normal Retirement Date',
    date: formatDate(
      addOrRemoveYearsToGivenDate(new Date(), 15),
      DateFormats.US
    ),
    age: 65,
    firstSubTitle: {
      title: 'Qualified Plan',
      amount: 2643.98,
      plans: [
        { title: 'Cash Balance Plan', amount: 355.21 },
        { title: 'Legacy Plan', amount: 2288.77 },
      ],
    },
    secondSubTitle: { title: 'Non-Qualified Plan ', amount: 602.95 },
  },
];

const DbUnpaidLeaveDashboardView = () => {
  const { fingertipLinks, companiesMessages } =
    useContext(PensionIndexStore) || {};
  const navigate = useNavigate();

  let date = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <Container className={'cnd-custom-container-large mb-12 px-lg-9 px-xl-0'}>
      <Row>
        <Col className="mt-7 mt-lg-11 col-12 col-lg-7">
          <DefaultCard
            className="h-100 flex-column flex-sm-row "
            variant="blue-motif"
            leftHeading={
              <div className="text-sm-20-lg-32">
                The easiest way to retire is online
              </div>
            }
            leftSubheading={
              <Fragment>
                <div className="text-sm-12-lg-14 fw-light mb-7">
                  You are eligible to commence your Pension Benefits. When you
                  are ready to retire, use the button below to start the
                  process.
                </div>
              </Fragment>
            }
            button={
              <Button
                className="align-self-end"
                variant="primary-shadow"
                size="sm"
                onClick={() =>
                  navigate(RETIRE_ROUTE.replace(':option', 'start'))
                }
              >
                Retire Online
              </Button>
            }
          />
        </Col>
        <Col className="mt-7 mt-lg-11 col-12 col-lg-5">
          <DefaultCard
            className="flex-column h-100"
            variant="blue"
            leftHeading={<div className="text-sm-20-lg-32">Current Events</div>}
            leftSubheading={
              <Container className="p-0 mb-7">
                <Row className="text-sm-12-lg-14 p-0">
                  <Col className=" text-nowrap col-12 fw-light text-sub1">
                    There are currently no events.
                  </Col>
                </Row>
              </Container>
            }
          />
        </Col>
      </Row>
      <Row className="m-0 p-0 d-flex justify-content-between">
        <Col className="m-0 p-0 mt-12 mt-lg-8 pt-lg-6 col-12 col-lg-7 pe-lg-4">
          <CurrentAccruedPension
            white
            currentAccruedPensionData={currentAccruedPensionData}
            linksData={linksDataCurrentAccruedPension}
          />
        </Col>
        <Col className="pe-lg-0 mt-12 mt-lg-8 pt-lg-3 col-12 col-lg-5">
          <FingerTipsLinks data={fingertipLinks} />
        </Col>
      </Row>

      <Row className="mt-12">
        <Col className="col-12 col-lg-7">
          <Col className="d-flex justify-content-between align-items-center mb-9">
            <h4 className="text-sm-20-lg-32 m-0">Total Rewards</h4>
            <Button
              variant="primary-outline"
              rightIcon={light('chevron-right')}
              size="sm"
              onClick={() => navigate(TOTAL_REWARDS_ROUTE)}
            >
              Details
            </Button>
          </Col>
          <Col>
            <Row className=" px-lg-0 px-7 flex-column align-items-center">
              <Col className="px-0 col-11 col-sm-8">
                <DonutChart
                  series={dataCompanyContributions.series}
                  options={dataCompanyContributions}
                />
              </Col>
              <Col className="pt-3 lh-xs  px-lg-10">
                <DonutCustomLegend options={dataCompanyContributions} />
              </Col>
            </Row>
          </Col>
        </Col>
      </Row>
      <Row className="my-13">
        <Col className="col-12 col-lg-7">
          <ChartCard
            title="Retirement Confidence"
            text={
              <p className="text-center text-lg-start  text-sub1 text-light-grey-normal fw-light lh-xs">
                Saving 8% will raise your Retirement Confidence level to 75%.
                Use the{' '}
                <Link
                  to={RETIREMENT_PLANNING_ROUTE}
                  className={
                    'fw-bold text-light-grey-normal text-decoration-none'
                  }
                >
                  Retirement Planning
                </Link>{' '}
                tool to further customize your strategy.
              </p>
            }
            button={
              <Button
                className="col-12 col-lg-7"
                variant="primary"
                rightIcon={light('person-cane')}
                size="sm"
                onClick={() => navigate(RETIREMENT_PLANNING_ROUTE)}
              >
                Retirement Planning
              </Button>
            }
          ></ChartCard>
        </Col>
        <Col className="col-12 col-lg-5 mt-13 mt-lg-0">
          <DefaultCard
            className="flex-column h-100"
            variant="white"
            leftHeading={
              <h5 className="text-fs-base fw-normal text-dark-gray text-center text-lg-start ">
                Pension Estimate
              </h5>
            }
            leftSubheading="Estimate your pension options at various ages."
            button={
              <div className="d-flex justify-content-end mt-10 flex-column flex-lg-row">
                <Button
                  className="mb-5 mb-lg-0"
                  variant="primary-outline"
                  size="sm"
                  onClick={() =>
                    navigate(PENSION_ROUTE.replace(':tab', 'saved'))
                  }
                >
                  Saved Estimates
                </Button>
                <Button
                  className="ms-0 ms-lg-5"
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(PENSION_ROUTE.replace(':tab', 'new'))}
                >
                  New Estimate
                </Button>
              </div>
            }
          />
        </Col>
      </Row>
      <Row>
        <Carousel data={companiesMessages} />
      </Row>
    </Container>
  );
};

function DbUnpaidLeaveDashboard() {
  return (
    <PensionIndexProvider>
      <DbUnpaidLeaveDashboardView />
    </PensionIndexProvider>
  );
}

export default DbUnpaidLeaveDashboard;
